import {useCallback, useMemo} from 'react'
import useIsMounted from '@github-ui/use-is-mounted'
import {throttle} from '@github/mini-throttle'
import {useAlive} from '@github-ui/use-alive'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useInvalidateOnAliveUpdate} from '@github-ui/use-invalidate-on-alive-update'
import {useChangeGroupsData, useChangeGroupsDataQueryKey} from '../page-data/loaders/use-change-groups-data'

type ChangeGroupsAliveData = {wait?: number; base_commit_oid?: string; head_commit_oid?: string}

export function useChangeGroupsLiveUpdates(
  channel: string,
  baseSha: string | undefined,
  headSha: string | undefined,
): void {
  const isMounted = useIsMounted()
  const {refetch} = useChangeGroupsData(headSha, baseSha)
  const queryKey = useChangeGroupsDataQueryKey(headSha, baseSha)
  const dedupeConcurrentRefetches = isFeatureEnabled('merge_status_checks_refetch_dedupe')

  const throttleRefetchingData = useMemo(
    () =>
      throttle(() => {
        if (isMounted()) {
          refetch()
        }
      }, 2000),
    [isMounted, refetch],
  )

  const handleChangeGroupChannelNotification = useMemo(
    () => (eventData: ChangeGroupsAliveData) => {
      // we only want to refetch if the current base and head sha are the same as the ones sent with the channel event
      if (eventData?.base_commit_oid === baseSha && eventData?.head_commit_oid === headSha) {
        window.setTimeout(() => throttleRefetchingData(), eventData.wait || 0)
      }
    },
    [baseSha, headSha, throttleRefetchingData],
  )
  // Original refetch path (flag OFF); empty channel no-ops it when ON.
  useAlive(dedupeConcurrentRefetches ? '' : channel, handleChangeGroupChannelNotification)

  const filter = useCallback(
    (data: ChangeGroupsAliveData) => data.base_commit_oid === baseSha && data.head_commit_oid === headSha,
    [baseSha, headSha],
  )

  // Additive dedupe path (flag ON); empty channel no-ops it when OFF.
  useInvalidateOnAliveUpdate(dedupeConcurrentRefetches ? channel : '', queryKey, {filter})
}
